import { Tile, TileSection } from 'foundations-components/transitional/components/Tile';
import { SkeletonValue } from './PropertySkeleton';
import { Flex } from 'foundations-components/components/Flex';
import { HR } from 'foundations-components/transitional/components/HR';
import { ScrollingColumn } from 'foundations-components/transitional/components/ScrollingColumn';
import styled, { css, keyframes } from 'styled-components';
import { List } from 'foundations-components/transitional/components/List';
import { LAST_SKELETON_VALUE_MARGIN_BOTTOM, TILE_PADDING, TILE_BORDER } from './ChicletSkeletonConstants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const ACTION_BAR_BUTTON_SIZE = 26;
const CONTENT_MAX_HEIGHT = 512;
const PROPERTY_SKELETON_HEIGHT = 16;
const PROPERTY_SKELETON_MARGIN_BOTTOM = 12;
const HEADER_CONTENT_GAP = 16;
const FOOTER_ACTION_HEIGHT_AND_MARGIN = 32;
const PRIMARY_DISPLAY_LABEL_ROW_HEIGHT = 32;
const ACTION_BAR_HEIGHT = 26;
const AVATAR_OR_ICON_SIZE = 32;
export const pulse = keyframes(["0%{opacity:1;}50%{opacity:0.6;}100%{opacity:1;}"]);
const ResponsiveGrid = styled.div.withConfig({
  displayName: "AssociationChicletListSkeleton__ResponsiveGrid",
  componentId: "sc-7kvpsk-0"
})(["display:grid;grid-template-columns:repeat( auto-fit,minmax(", "px,1fr) );column-gap:", ";row-gap:", ";"], props => props.gridChicletSize, props => props.theme.space['300'], props => props.theme.space['300']);
const AvatarOrIconSkeletonDiv = styled.div.withConfig({
  displayName: "AssociationChicletListSkeleton__AvatarOrIconSkeletonDiv",
  componentId: "sc-7kvpsk-1"
})(["width:", "px;height:", "px;border-radius:50%;background-color:", ";", " flex-shrink:0;"], AVATAR_OR_ICON_SIZE, AVATAR_OR_ICON_SIZE, props => props.theme.color['fill-field-disabled'], css(["animation:", " 2s ease-in-out infinite;"], pulse));
const ActionButtonSkeletonDiv = styled.div.withConfig({
  displayName: "AssociationChicletListSkeleton__ActionButtonSkeletonDiv",
  componentId: "sc-7kvpsk-2"
})(["width:", "px;height:", "px;border-radius:3px;background-color:", ";", " flex-shrink:0;"], 22, 18, props => props.theme.color['fill-field-disabled'], css(["animation:", " 2s ease-in-out infinite;"], pulse));
const TitleSkeletonDiv = styled.div.withConfig({
  displayName: "AssociationChicletListSkeleton__TitleSkeletonDiv",
  componentId: "sc-7kvpsk-3"
})(["height:", ";border-radius:3px;background-color:", ";", " flex:0.85;min-width:80px;"], props => props.theme.space['400'], props => props.theme.color['fill-field-disabled'], css(["animation:", " 2s ease-in-out infinite;"], pulse));
export function getAssociationChicletListSkeletonHeight(loadingComponentProps) {
  const {
    numberOfSkeletons,
    numberOfProperties
  } = loadingComponentProps;
  const tileHeight = HEADER_CONTENT_GAP + TILE_PADDING + TILE_BORDER + PRIMARY_DISPLAY_LABEL_ROW_HEIGHT + numberOfProperties * PROPERTY_SKELETON_HEIGHT + (numberOfProperties - 1) * PROPERTY_SKELETON_MARGIN_BOTTOM + LAST_SKELETON_VALUE_MARGIN_BOTTOM + FOOTER_ACTION_HEIGHT_AND_MARGIN;
  const contentHeight = ACTION_BAR_HEIGHT + numberOfSkeletons * tileHeight;
  return Math.min(contentHeight, CONTENT_MAX_HEIGHT);
}
export function AssociationChicletContentSkeleton({
  cardId,
  numberOfProperties
}) {
  return /*#__PURE__*/_jsxs(Flex, {
    flexDirection: "column",
    gap: "200",
    children: [/*#__PURE__*/_jsxs(Flex, {
      "data-test-id": "chiclet-skeleton-header",
      justifyContent: "space-between",
      alignItems: "center",
      inlineSize: "100",
      gap: "200",
      children: [/*#__PURE__*/_jsxs(Flex, {
        alignItems: "center",
        gap: "300",
        flexGrow: 1,
        children: [/*#__PURE__*/_jsx(AvatarOrIconSkeletonDiv, {
          "data-test-id": "chiclet-skeleton-avatar-or-icon"
        }), /*#__PURE__*/_jsx(TitleSkeletonDiv, {
          "data-test-id": "chiclet-skeleton-title"
        })]
      }), /*#__PURE__*/_jsxs(Flex, {
        gap: "200",
        alignItems: "center",
        "data-test-id": "chiclet-skeleton-action-bar",
        children: [/*#__PURE__*/_jsx(ActionButtonSkeletonDiv, {}), /*#__PURE__*/_jsx(ActionButtonSkeletonDiv, {})]
      })]
    }), /*#__PURE__*/_jsx(HR, {
      marginBlock: "100",
      backgroundColor: "border-core-subtle"
    }), /*#__PURE__*/_jsxs(List, {
      styled: false,
      children: [/*#__PURE__*/_jsx(Flex, {
        flexDirection: "column",
        children: Array.from({
          length: numberOfProperties
        }).map((_, _index) => /*#__PURE__*/_jsx(SkeletonValue, {
          "data-test-id": "chiclet-skeleton-value",
          width: "65%",
          marginTop: 0,
          marginBottom: 12
        }, `chiclet-skeleton-prop-${_index}`))
      }), /*#__PURE__*/_jsx(SkeletonValue, {
        "data-test-id": "chiclet-skeleton-labels",
        width: "50%",
        marginTop: 0,
        marginBottom: LAST_SKELETON_VALUE_MARGIN_BOTTOM
      }, `chiclet-skeleton-labels`)]
    }, `chiclet-skeleton-${cardId}`)]
  }, `association-chiclet-skeleton-${cardId}`);
}
function AssociationChicletListSkeleton({
  cardId,
  numberOfSkeletons,
  numberOfProperties,
  gridChicletSize
}) {
  const skeletons = Array.from({
    length: numberOfSkeletons
  });
  const tiles = skeletons.map((__, index) => /*#__PURE__*/_jsx(Tile, {
    "data-test-id": "chiclet",
    compact: true,
    marginBlockStart: "0",
    marginBlockEnd: "0",
    paddingInline: "200",
    paddingBlockStart: "200",
    paddingBlockEnd: "200",
    distance: "small",
    children: /*#__PURE__*/_jsx(TileSection, {
      padding: "0",
      children: /*#__PURE__*/_jsx(AssociationChicletContentSkeleton, {
        cardId: cardId,
        numberOfProperties: numberOfProperties
      })
    })
  }, `chiclet-skeleton-${index}`));
  return /*#__PURE__*/_jsxs(Flex, {
    maxBlockSize: `${CONTENT_MAX_HEIGHT}px`,
    flexDirection: "column",
    gap: "200",
    children: [/*#__PURE__*/_jsx(Flex, {
      flexDirection: "column",
      "data-test-id": "chiclet-skeleton-content-action-bar",
      marginBlockStart: "0",
      marginBlockEnd: "200",
      children: /*#__PURE__*/_jsxs(Flex, {
        flexDirection: "row",
        alignItems: "center",
        gap: "300",
        children: [/*#__PURE__*/_jsx(SkeletonValue, {
          width: ACTION_BAR_BUTTON_SIZE,
          marginTop: 0,
          marginBottom: 0,
          height: ACTION_BAR_BUTTON_SIZE
        }), /*#__PURE__*/_jsx(SkeletonValue, {
          marginTop: 0,
          marginBottom: 0
        })]
      })
    }), /*#__PURE__*/_jsx(ScrollingColumn, {
      maxHeight: "412px",
      children: /*#__PURE__*/_jsx(ResponsiveGrid, {
        gridChicletSize: gridChicletSize,
        "data-loading": true,
        "data-test-id": "association-chiclet-list-skeleton",
        role: "status",
        id: `association-chiclet-skeleton-list-${cardId}`,
        children: tiles
      })
    }), /*#__PURE__*/_jsx(SkeletonValue, {
      width: "35%",
      marginTop: 16
    })]
  });
}
export default AssociationChicletListSkeleton;